/**
 * Turns the workbook a user picked into something the import endpoints can actually read.
 *
 * WHY THIS EXISTS. `Users/CreateMultiUsers` does not parse Excel. It runs every upload
 * through a `StreamReader` and splits each "line" on a tab or a comma — so an `.xlsx`,
 * which is a ZIP archive, is read as its own compressed bytes: the stray tabs inside the
 * archive produce rows of binary, every one of them fails validation, and each is echoed
 * back into the failure dialog. That is the wall of `??????` an administrator sees.
 *
 * The CSV path of the same endpoint is correct. So the workbook is read HERE and handed
 * over as CSV, and the endpoint is left alone (R2).
 *
 * IT ALSO FIXES THE COLUMN ORDER, which is the second half of the same problem: the
 * templates and the handler disagree about what each column means (see
 * `API_FINDINGS.md`). Reading the sheet in the browser is the only place that can put the
 * cells where the endpoint expects them, so each caller passes a mapper — a row of the
 * TEMPLATE in, a row the ENDPOINT reads out.
 */

/** `.xlsx` / `.xls`. A file already in CSV goes to the server untouched. */
export const isWorkbook = (file) => /\.(xlsx|xls)$/i.test(file?.name ?? '')

/* LOADED ON DEMAND. The parser is ~900KB — larger than most of this app's route chunks —
   and it is needed by exactly one dialog, at the moment a file is chosen. Importing it at
   the top would put it in the entry chunk and make every page load carry it, which is the
   same reason ECharts and video.js are split out. */
const xlsx = () => import('xlsx')

/** U+FEFF. See the note in `toCsvFile`. */
const BOM = String.fromCharCode(0xfeff)

/**
 * The first sheet, as rows of trimmed strings, HEADER ROW INCLUDED — the endpoints skip
 * their own first row, so it has to still be there when the file arrives.
 *
 * `cellDates` so a real date cell arrives as a Date rather than Excel's serial number;
 * `defval` so a blank cell holds its position instead of shortening the row and shifting
 * every column after it.
 */
export async function readWorkbookRows(file) {
  const { read, utils } = await xlsx()

  const workbook = read(await file.arrayBuffer(), { type: 'array', cellDates: true })
  const sheet = workbook.Sheets[workbook.SheetNames[0]]
  if (!sheet) return []

  return utils
    .sheet_to_json(sheet, { header: 1, blankrows: false, defval: '' })
    .map((row) => row.map(cellText))
}

/** ISO for a date cell, the trimmed text for everything else. */
function cellText(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return isoDate(value)
  return String(value ?? '').trim()
}

/** `YYYY-MM-DD`, in LOCAL time — `toISOString()` would shift the day across a timezone. */
export function isoDate(date) {
  const d = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`
}

/**
 * Rows out as a CSV file.
 *
 * COMMAS ARE REPLACED, NOT QUOTED. The endpoint splits on `,` with `String.Split` and
 * knows nothing about quoting, so a quoted field would arrive as two broken columns.
 * Replacing the character keeps the row aligned, which matters far more than the comma —
 * and a comma inside a username or a department name is vanishingly rare.
 *
 * The BOM is deliberate: `StreamReader` detects the encoding from it, which is what keeps
 * Arabic names intact. It sits on the header row, which every endpoint discards.
 */
export function toCsvFile(rows, fileName = 'import.csv') {
  const cell = (value) =>
    String(value ?? '')
      .replace(/[\r\n,]+/g, ' ')
      // The replacement above turns ", " into two spaces; a name is not stored with them.
      .replace(/\s{2,}/g, ' ')
      .trim()

  const csv = rows.map((row) => row.map(cell).join(',')).join('\r\n')

  /* Built from a char code rather than typed in: a literal U+FEFF in a source file is
     invisible, so nobody can see whether it is there. Note that `Blob.text()` STRIPS a
     leading BOM when it decodes, so checking for one that way always says it is missing —
     read the bytes. */
  return new File([BOM + csv], fileName, { type: 'text/csv' })
}

/**
 * The whole conversion, for a service module to call before it uploads.
 *
 * @param {File} file
 * @param {(row: string[], index: number) => string[]} [mapRow]  template row -> endpoint
 *        row. The header is passed through it too, so the file still has one.
 * @returns {Promise<File>} the CSV, or the original file when it was not a workbook
 */
export async function workbookToCsv(file, mapRow) {
  if (!isWorkbook(file)) return file

  const rows = await readWorkbookRows(file)
  const mapped = mapRow ? rows.map(mapRow) : rows

  return toCsvFile(mapped, `${file.name.replace(/\.[^.]+$/, '')}.csv`)
}
